import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  ShoppingBag,
  Package,
  Wrench,
  Hammer,
  Calculator,
  Sparkles,
  Layers,
  Printer,
  DollarSign,
  Settings,
  Search,
  Bell,
  ChevronDown,
  ChevronRight,
  PanelLeft,
  LogOut,
  ExternalLink,
  Box,
  X,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from 'next-themes';

export default function AdminLayout() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { resolvedTheme } = useTheme();

  // Sidebar collapse state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('mila_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  // Accordion state for E-commerce and Herramientas groups
  const [ecommerceOpen, setEcommerceOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(true);

  // Mobile drawer state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // STRICT REQUIREMENT: Only admin uses dark mode.
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      root.style.colorScheme = 'light';
    }
  }, [resolvedTheme]);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('mila_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const userInitial = user?.email ? user.email.charAt(0).toUpperCase() : 'M';

  // Active checks for parent groups
  const isEcommerceActive =
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/products-admin') ||
    pathname.startsWith('/orders') ||
    pathname.startsWith('/inventory') ||
    pathname.startsWith('/filaments') ||
    pathname.startsWith('/customers');

  const isToolsActive =
    pathname.startsWith('/tools') ||
    pathname.startsWith('/workshop') ||
    pathname.startsWith('/calculator') ||
    pathname.startsWith('/generator') ||
    pathname.startsWith('/printers');

  return (
    <div className="flex h-screen bg-background text-foreground w-full overflow-hidden transition-colors duration-200 font-sans">
      {/* ── Sidebar (Exact Shadcn E-commerce Dashboard Style) ── */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r border-border bg-card shrink-0 transition-all duration-300 ease-in-out relative z-30 select-none",
          isCollapsed ? "w-[68px]" : "w-64"
        )}
      >
        {/* Brand Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          <Link
            to="/dashboard"
            className={cn(
              "flex items-center gap-2.5 overflow-hidden transition-all",
              isCollapsed && "justify-center w-full"
            )}
          >
            {/* Geometric logo icon (like shadcn cube) */}
            <div className="h-7 w-7 rounded-lg bg-foreground text-background flex items-center justify-center font-bold text-xs shadow-xs shrink-0">
              <Box className="h-4 w-4" />
            </div>
            {!isCollapsed && (
              <span className="font-bold tracking-tight text-foreground text-sm leading-none">
                MilaOS
              </span>
            )}
          </Link>
        </div>

        {/* Search Bar in Sidebar (as in screenshot) */}
        {!isCollapsed && (
          <div className="px-3 pt-3 pb-1">
            <div className="relative flex items-center w-full px-2.5 py-1.5 rounded-lg border border-border bg-muted/40 text-xs text-muted-foreground hover:bg-muted/60 transition-colors cursor-pointer">
              <Search className="h-3.5 w-3.5 mr-2 shrink-0 opacity-70" />
              <span className="flex-1 text-[11px]">Buscar...</span>
              <kbd className="text-[10px] bg-background text-muted-foreground px-1.5 py-0.5 rounded border border-border font-mono shadow-2xs">
                ⌘K
              </kbd>
            </div>
          </div>
        )}

        {/* Sidebar Nav Items */}
        <div className="flex-1 px-3 py-3 space-y-4 overflow-y-auto overflow-x-hidden scrollbar-hide text-xs">
          {/* Section: Dashboards */}
          <div className="space-y-1">
            {!isCollapsed && (
              <h3 className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                Dashboards
              </h3>
            )}

            {/* E-commerce collapsible group */}
            <div>
              <button
                onClick={() => setEcommerceOpen(!ecommerceOpen)}
                title={isCollapsed ? "E-commerce" : undefined}
                className={cn(
                  "w-full flex items-center rounded-lg px-2.5 py-2 font-medium transition-all group",
                  isCollapsed ? "justify-center h-10 w-10 mx-auto" : "justify-between",
                  isEcommerceActive
                    ? "text-foreground font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <ShoppingBag className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                  {!isCollapsed && <span className="truncate">E-commerce</span>}
                </div>
                {!isCollapsed && (
                  ecommerceOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
                  )
                )}
              </button>

              {/* Submenu items */}
              {!isCollapsed && ecommerceOpen && (
                <div className="mt-1 space-y-0.5 pl-6 border-l border-border/60 ml-4">
                  <Link
                    to="/dashboard"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname === '/dashboard'
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/products-admin"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname === '/products-admin'
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Lista de Productos
                  </Link>
                  <Link
                    to="/products-admin/detail"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/products-admin/') && pathname !== '/products-admin/new'
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Detalle de Producto
                  </Link>
                  <Link
                    to="/products-admin/new"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname === '/products-admin/new'
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Añadir Producto
                  </Link>
                  <Link
                    to="/orders"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname === '/orders'
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Lista de Pedidos
                  </Link>
                  <Link
                    to="/orders/detail"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/orders/')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Detalle de Pedido
                  </Link>
                  <Link
                    to="/customers"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/customers')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Clientes
                  </Link>
                  <Link
                    to="/inventory"
                    className={cn(
                      "block rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/inventory') || pathname.startsWith('/filaments')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    Inventario & Stock
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Section: Herramientas */}
          <div className="space-y-1">
            {!isCollapsed && (
              <h3 className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                Herramientas
              </h3>
            )}

            <div>
              <button
                onClick={() => setToolsOpen(!toolsOpen)}
                title={isCollapsed ? "Herramientas" : undefined}
                className={cn(
                  "w-full flex items-center rounded-lg px-2.5 py-2 font-medium transition-all group",
                  isCollapsed ? "justify-center h-10 w-10 mx-auto" : "justify-between",
                  isToolsActive
                    ? "text-foreground font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Wrench className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                  {!isCollapsed && <span className="truncate">Herramientas</span>}
                </div>
                {!isCollapsed && (
                  toolsOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
                  )
                )}
              </button>

              {/* Submenu items */}
              {!isCollapsed && toolsOpen && (
                <div className="mt-1 space-y-0.5 pl-6 border-l border-border/60 ml-4">
                  <Link
                    to="/tools"
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname === '/tools' || pathname.startsWith('/tools/')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <span>Herramientas (Hub)</span>
                    <Sparkles className="h-3 w-3 opacity-60" />
                  </Link>
                  <Link
                    to="/workshop"
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/workshop')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <span>Taller en Vivo</span>
                    <Hammer className="h-3 w-3 opacity-60" />
                  </Link>
                  <Link
                    to="/calculator"
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/calculator')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <span>Calculadora 3D</span>
                    <Calculator className="h-3 w-3 opacity-60" />
                  </Link>
                  <Link
                    to="/inventory"
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/inventory') || pathname.startsWith('/filaments')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <span>Inventario</span>
                    <Layers className="h-3 w-3 opacity-60" />
                  </Link>
                  <Link
                    to="/printers"
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      pathname.startsWith('/printers')
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <span>Impresoras 3D</span>
                    <Printer className="h-3 w-3 opacity-60" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Section: Finanzas & Configuración */}
          <div className="space-y-1">
            {!isCollapsed && (
              <h3 className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                Administración
              </h3>
            )}
            <Link
              to="/expenses"
              title={isCollapsed ? "Gastos" : undefined}
              className={cn(
                "flex items-center rounded-lg px-2.5 py-2 font-medium transition-colors gap-2.5 group",
                isCollapsed && "justify-center h-10 w-10 mx-auto",
                pathname.startsWith('/expenses')
                  ? "bg-muted text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              {!isCollapsed && <span className="truncate">Control de Gastos</span>}
            </Link>
            <Link
              to="/settings"
              title={isCollapsed ? "Configuración" : undefined}
              className={cn(
                "flex items-center rounded-lg px-2.5 py-2 font-medium transition-colors gap-2.5 group",
                isCollapsed && "justify-center h-10 w-10 mx-auto",
                pathname.startsWith('/settings')
                  ? "bg-muted text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Settings className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              {!isCollapsed && <span className="truncate">Configuración</span>}
            </Link>
          </div>
        </div>

        {/* Sidebar Bottom Card (like 'Get This Template' in screenshot) */}
        {!isCollapsed && (
          <div className="p-3 border-t border-border mt-auto">
            <div className="p-3 rounded-xl border border-border bg-muted/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-xs text-foreground">Mila OS Taller</span>
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Producción 3D y catálogo sincronizado en tiempo real.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-[11px] font-semibold bg-background border-border hover:bg-muted"
                asChild
              >
                <Link to="/workshop">Ver Taller en Vivo</Link>
              </Button>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main Workspace Column ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-background">
        {/* ── Top Header Navbar (Exact Shadcn E-commerce Header) ── */}
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-4 sm:px-6 backdrop-blur-md transition-colors">
          {/* Left: Sidebar Toggle & Outlet Chip (like 'Shadcn Outlet') */}
          <div className="flex items-center gap-3">
            {/* Sidebar toggle button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg -ml-1"
              title="Alternar barra lateral"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>

            {/* Mobile hamburger trigger */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
              title="Abrir menú móvil"
            >
              <Menu className="h-4 w-4" />
            </Button>

            {/* Outlet Pill / Chip (like orange dot + 'Shadcn Outlet' in screenshot) */}
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-md border border-border bg-background text-xs font-medium text-foreground hover:bg-muted/50 transition-colors shadow-2xs">
              <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
              <span className="truncate">Mila Taller</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground ml-0.5" />
            </div>
          </div>

          {/* Right: Actions, Notifications, Theme Toggle & Profile Avatar */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Download / Ver Tienda link button */}
            <Link
              to="/tienda"
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mr-1 hidden sm:inline-flex items-center gap-1"
              title="Abrir tienda pública"
            >
              <span>Ver Tienda</span>
              <ExternalLink className="h-3 w-3 opacity-70" />
            </Link>

            {/* Notifications Bell */}
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
              title="Notificaciones"
            >
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
            </Button>

            {/* Theme Toggle (Exclusive for Admin) */}
            <ThemeToggle
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
            />

            {/* User Profile Avatar with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 rounded-full overflow-hidden border border-border hover:ring-2 hover:ring-primary/20 transition-all ml-1"
                >
                  <Avatar className="h-full w-full">
                    <AvatarFallback className="bg-muted text-foreground font-bold text-xs">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 mt-2" align="end" forceMount>
                <DropdownMenuLabel className="font-normal p-3">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-semibold leading-none truncate text-foreground">
                      {user?.email || 'Admin Mila'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      Administrador del Taller
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <Link to="/products-admin">
                  <DropdownMenuItem className="cursor-pointer py-2 text-xs">
                    <Package className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>Catálogo & Productos</span>
                  </DropdownMenuItem>
                </Link>
                <Link to="/inventory">
                  <DropdownMenuItem className="cursor-pointer py-2 text-xs">
                    <Layers className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>Inventario & Stock</span>
                  </DropdownMenuItem>
                </Link>
                <Link to="/tools">
                  <DropdownMenuItem className="cursor-pointer py-2 text-xs">
                    <Wrench className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>Herramientas (Hub)</span>
                  </DropdownMenuItem>
                </Link>
                <Link to="/settings">
                  <DropdownMenuItem className="cursor-pointer py-2 text-xs">
                    <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>Configuración</span>
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive cursor-pointer py-2 text-xs focus:text-destructive focus:bg-destructive/10"
                  onClick={() => logout()}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Cerrar Sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* ── Main Content Area ── */}
        <main className="flex-1 overflow-y-auto bg-muted/20 transition-colors">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
            <Outlet />
          </div>
        </main>

        {/* ── Mobile Slide-Over Drawer ── */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex animate-in fade-in duration-200">
            <div
              className="fixed inset-0 bg-background/80 backdrop-blur-xs transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            />

            <div className="relative flex flex-col w-5/6 max-w-xs bg-card border-r border-border p-5 shadow-2xl z-50 overflow-y-auto text-xs">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-foreground text-background flex items-center justify-center font-bold text-xs">
                    <Box className="h-4 w-4" />
                  </div>
                  <span className="font-bold text-sm text-foreground">MilaOS</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(false)}
                  className="h-8 w-8 rounded-lg"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="py-4 space-y-5 flex-1">
                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                    E-commerce
                  </h4>
                  <div className="space-y-1">
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname === '/dashboard' ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/products-admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname === '/products-admin' ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Lista de Productos
                    </Link>
                    <Link
                      to="/products-admin/detail"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/products-admin/') && pathname !== '/products-admin/new' ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Detalle de Producto
                    </Link>
                    <Link
                      to="/products-admin/new"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname === '/products-admin/new' ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Añadir Producto
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname === '/orders' ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Lista de Pedidos
                    </Link>
                    <Link
                      to="/orders/detail"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/orders/') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Detalle de Pedido
                    </Link>
                    <Link
                      to="/customers"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/customers') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Clientes
                    </Link>
                    <Link
                      to="/inventory"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/inventory') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Inventario & Stock
                    </Link>
                  </div>
                </div>

                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                    Herramientas
                  </h4>
                  <div className="space-y-1">
                    <Link
                      to="/tools"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname === '/tools' || pathname.startsWith('/tools/') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Herramientas (Hub)
                    </Link>
                    <Link
                      to="/workshop"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/workshop') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Taller en Vivo
                    </Link>
                    <Link
                      to="/calculator"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/calculator') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Calculadora 3D
                    </Link>
                    <Link
                      to="/inventory"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/inventory') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Inventario
                    </Link>
                    <Link
                      to="/printers"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/printers') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Impresoras 3D
                    </Link>
                  </div>
                </div>

                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                    Administración
                  </h4>
                  <div className="space-y-1">
                    <Link
                      to="/expenses"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/expenses') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Control de Gastos
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-3 py-2 rounded-lg font-medium",
                        pathname.startsWith('/settings') ? "bg-muted font-semibold" : "text-muted-foreground"
                      )}
                    >
                      Configuración
                    </Link>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border space-y-3">
                <div className="flex items-center justify-between px-1">
                  <span className="text-muted-foreground font-medium">Tema visual</span>
                  <ThemeToggle variant="outline" className="h-8 w-8" />
                </div>
                <Button
                  variant="destructive"
                  className="w-full justify-start gap-2 h-9 text-xs"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Cerrar Sesión</span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
